import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@ankr.com/ui';
import { Paper } from '@mui/material';
import Typography from '@mui/material/Typography';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { Back, NumberField, useChainData, ZERO } from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import { Layout } from 'modules/layout';
import {
  useGetAccountAddressQuery,
  useGetAccountBalanceQuery,
  useGetNativeBalanceQuery,
  useGetNativeTokenUSDPriceQuery,
} from 'modules/store';

import { DashboardRoutesConfig } from '../../dashboard';
import { useDepositMutation } from '../actions/deposit';
import { useGetAccountMaxValuesQuery } from '../actions/getAccountMaxValues';
import { useWithdrawMutation } from '../actions/withdraw';
import translation from './account.translation';

interface FormValues {
  deposit: string;
  withdraw: string;
}

const mergedTranslation = mergeTranslations(translation, globalTranslation);

export function Account(): JSX.Element {
  const { classes } = useStyles();
  const mountedRef = useRef(false);
  const { keys, t } = useTranslation(mergedTranslation);
  const chainData = useChainData();
  const [deposit, { isLoading: isDepositLoading }] = useDepositMutation();
  const [withdraw, { isLoading: isWithdrawLoading }] = useWithdrawMutation();

  const chainId = chainData?.chainId || null;
  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const { data: accountBalance = ZERO } = useGetAccountBalanceQuery({
    accountAddress,
  });
  const { data: USDPrice = ZERO } = useGetNativeTokenUSDPriceQuery({
    chainId,
  });
  const USDAccountBalance = accountBalance.multipliedBy(USDPrice);
  const { data: nativeBalance = ZERO } = useGetNativeBalanceQuery({
    accountAddress,
  });

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    resetField,
  } = useForm<FormValues>({
    mode: 'onChange',
  });

  const depositFormValue = watch('deposit');
  const depositAmount = useMemo(() => {
    const convertedValue = new BigNumber(depositFormValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [depositFormValue]);

  const withdrawFormValue = watch('withdraw');
  const withdrawAmount = useMemo(() => {
    const convertedValue = new BigNumber(withdrawFormValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [withdrawFormValue]);

  const depositDisabled = depositAmount?.isZero() || !!errors?.deposit;
  const withdrawDisabled = withdrawAmount?.isZero() || !!errors?.withdraw;

  const { data } = useGetAccountMaxValuesQuery({
    currentAccount: accountAddress,
    depositBalance: nativeBalance,
    withdrawBalance: accountBalance,
  });

  const { depositMaxValue = ZERO, withdrawMaxValue = ZERO } = data ?? {};

  const trimmedUSDAccountBalance = USDAccountBalance?.decimalPlaces(2);
  const isValidUSDAccountBalance =
    trimmedUSDAccountBalance &&
    !trimmedUSDAccountBalance.isZero() &&
    !trimmedUSDAccountBalance.isNaN();

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <Layout>
      <div className={classes.container}>
        <Back
          link={DashboardRoutesConfig.dashboard.generatePath()}
          className={classes.back}
        />

        <Paper
          className={classes.content}
          component="form"
          data-tid="account-form"
        >
          <Typography variant="h6" data-tid="account-balance">
            {t(keys.units.tokenAmount, {
              amount: accountBalance?.decimalPlaces(4).toString(),
              token: chainData?.tokenName,
            })}

            {isValidUSDAccountBalance &&
              ` (${t(keys.units.$, {
                value: trimmedUSDAccountBalance.toString(),
              })})`}
          </Typography>

          <Typography
            variant="body3"
            mt={1}
            sx={theme => ({ color: theme.palette.grey[600] })}
          >
            {t(keys.balance)}
          </Typography>

          <div className={classes.area}>
            <NumberField
              name="deposit"
              data-tid="account-deposit-input"
              control={control}
              className={classes.input}
              error={!!errors?.deposit}
              balance={nativeBalance}
              label={t(keys.deposit)}
              endLabel={t(keys.walletBalance, {
                value: depositMaxValue?.decimalPlaces(4).toString(),
                unit: chainData?.tokenName,
              })}
              variant="filled"
              InputProps={{
                endAdornment: (
                  <Typography
                    component="button"
                    type="button"
                    className={classes.max}
                    variant="body4"
                    onClick={() =>
                      setValue('deposit', depositMaxValue?.toString() ?? '0')
                    }
                  >
                    {t(keys.max)}
                  </Typography>
                ),
              }}
            />

            <LoadingButton
              data-tid="account-deposit-button"
              className={classes.actionButton}
              disabled={depositDisabled}
              loading={isDepositLoading}
              onClick={async () => {
                await deposit({
                  chainId,
                  accountAddress,
                  amount: depositAmount,
                });
                if (!mountedRef.current) {
                  return;
                }

                resetField('deposit');
              }}
              size="large"
              type="button"
            >
              {t(keys.deposit)}
            </LoadingButton>
          </div>

          <div className={classes.area}>
            <NumberField
              name="withdraw"
              data-tid="account-withdraw-input"
              variant="filled"
              control={control}
              className={classes.input}
              balance={accountBalance}
              error={!!errors?.withdraw}
              label={t(keys.withdraw)}
              endLabel={t(keys.availableBalance, {
                value: withdrawMaxValue?.decimalPlaces(4).toString(),
                unit: chainData?.tokenName,
              })}
              InputProps={{
                endAdornment: (
                  <Typography
                    component="button"
                    type="button"
                    className={classes.max}
                    variant="body4"
                    onClick={() =>
                      setValue('withdraw', withdrawMaxValue?.toString() ?? '0')
                    }
                  >
                    {t(keys.max)}
                  </Typography>
                ),
              }}
            />

            <LoadingButton
              data-tid="account-withdraw-button"
              className={classes.actionButton}
              disabled={withdrawDisabled}
              loading={isWithdrawLoading}
              onClick={async () => {
                await withdraw({
                  chainId,
                  accountAddress,
                  amount: withdrawAmount,
                });
                if (!mountedRef.current) {
                  return;
                }

                resetField('withdraw');
              }}
              size="large"
              variant="outlined"
              type="button"
            >
              {t(keys.withdraw)}
            </LoadingButton>
          </div>
        </Paper>
      </div>
    </Layout>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    margin: `${theme.spacing(11)} auto`,
    width: '100%',
    maxWidth: 756,
  },

  back: {
    marginBottom: theme.spacing(8),
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: theme.spacing(15),

    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(8),
    },

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(8, 4),
    },
  },

  area: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(5),
    marginTop: theme.spacing(5),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'initial',
    },
  },

  input: {
    width: '100%',
  },

  actionButton: {
    '&&': {
      minWidth: 124,
    },
  },

  max: {
    '&&': {
      minWidth: 50,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.common.white,
      borderRadius: 6,
      color: theme.palette.grey[500],
      cursor: 'pointer',
      border: 'none',
    },
  },
}));
